import { loan_application_api } from '@axo/shared/data-access/api';
import { ExpenseKind, loan_application } from '@axo/shared/data-access/types';
import { Handler } from '../api-orchestrator';
import { IPreconditions } from '../types/IPreconditions';

interface IDependencies {
  postExpense: typeof loan_application_api.postExpense;
  patchExpense: typeof loan_application_api.patchExpense;
}

export const expenseHandlerFactory =
  ({
    postExpense,
    patchExpense,
  }: IDependencies): Handler<loan_application.Expense, IPreconditions> =>
  async ({
    data,
    preconditions: {
      customerId,
      customerJWT,
      loanApplicationId,
      ids,
      expenses,
    },
    baseUrl,
    frontendId,
  }) => {
    if (!frontendId.startsWith('Expense')) {
      return {
        didSync: false,
      };
    }

    const kind = frontendId.split('/')[1];

    if (!(kind in ExpenseKind)) {
      return {
        didSync: false,
      };
    }

    data.Kind = ExpenseKind[kind as keyof typeof ExpenseKind];

    if (!customerId || !customerJWT || !loanApplicationId) {
      return { didSync: false };
    }

    const id = ids.expenses?.[frontendId];
    const storedExpense = expenses?.find((expense) => expense.ID === id);
    const api = { url: { service: baseUrl }, token: customerJWT };

    if (id) {
      if (storedExpense?.Value === data.Value) {
        return {
          didSync: false,
        };
      }

      const patched = await patchExpense(api)(loanApplicationId, id, data);

      const updatedExpenses = expenses?.map((expense) => {
        if (expense.ID === id) {
          return patched;
        }

        return expense;
      });

      return {
        didSync: true,
        newPreconditions: {
          expenses: updatedExpenses,
        },
      };
    }

    const expense = await postExpense(api)(loanApplicationId, data);

    const updatedExpenses = expenses ? [...expenses, expense] : [expense];

    return {
      didSync: true,
      newPreconditions: {
        ids: {
          ...ids,
          expenses: {
            ...ids.expenses,
            [frontendId]: expense.ID,
          },
        },
        expenses: updatedExpenses,
      },
    };
  };
